import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { loadStripe } from '@stripe/stripe-js';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD } from 'src/app/shared/routes';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { MANAGED_USER_IDS_FOR_WHICH_STRIPE_IS_DISABLED } from '../constants';
import { DqfDocument } from '../models/dqfDocument';
import { DriverQualificationService } from '../service/driver-qualification.service';

const SOURCE_TEXT = 'self';
const REPORT_TYPE = 'PoliceClearance';
const EXTENSION = 'pdf';
const POLICE_CLEARANCE_CERTIFICATE = 'PoliceClearanceCertificate';
const DOCUMENT_TYPE = 'Criminal Record (Background) Check';

@Component({
  selector: 'app-dqf-processing-background-request',
  templateUrl: './dqf-processing-background-request.component.html',
  styleUrls: ['./dqf-processing-background-request.component.scss'],
  standalone: false
})
export class DqfProcessingBackgroundRequestComponent implements OnInit {
  documentName: string;
  errorMessage: string;
  driverEmail: string;
  paymentSessionId: any;
  driver: any;
  paymentOrders: any;
  loading: boolean;
  dqfId: string;
  initiateStripe: any;
  dqf: any;
  enableStripePayment: boolean;
  isStripeSessionActive = false;
  publicKey: any;
  //checkout: any;
  isCheckoutMounted = false;
  @ViewChild('checkoutContainer', { static: false }) checkoutRef!: ElementRef<HTMLDivElement>;
  checkout = null;

  constructor(
    private readonly registrationService: RegistrationService,
    private readonly router: Router,
    private route: ActivatedRoute,
    private dqfService: DriverQualificationService,
    private readonly toastService: ToastService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.documentName = params.documentType;
      this.initiateStripe = params?.stripe;
    });
    this.getDqf();
    this.driver = this.registrationService.getDriver();
    this.driverEmail = this.driver?.email;
    this.dqfId = this.dqfService.dqfId;
    const returnRoute = sessionStorage.getItem('postStripeReturnRoute');
    if (returnRoute) {
      setTimeout(() => {
        this.navigateBackToDqfDocuments();
      }, 200);
    }
  }

  ngOnDestroy(): void {
    this.safeUnmountCheckout();
  }

  getDqf(): void {
    this.loading = true;
    const dqfId = this.dqfService.dqfId;
    this.dqfService.getDqfById(dqfId).subscribe((response: DqfDocument) => {
      if (response) {
        this.dqf = response;
      }
      this.loading = false;
      this.enableStripePayment =
        !!this.initiateStripe &&
        !MANAGED_USER_IDS_FOR_WHICH_STRIPE_IS_DISABLED.includes(this.dqf?.managedUserCompanyId);
    });
  }

  navigateBack() {
    this.safeUnmountCheckout();
    this.isStripeSessionActive = false;
    setTimeout(() => {
      this.navigateBackToDqfDocuments();
    }, 200);
  }

  private navigateBackToDqfDocuments(): void {
    const dqfId = this.dqfService.dqfId || this.dqfId || sessionStorage.getItem('currentDqfId');
    if (dqfId) {
      this.router.navigate([ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD, dqfId]);
    } else {
      this.router.navigate([ROUTE_DQF]);
    }
  }

  onSwipeRight(event: MouseEvent) {
    event.preventDefault();
  }

  initiatePayment() {
    this.safeUnmountCheckout();

    setTimeout(async () => {
      await this.renderStripeCheckout(); // call init after delay
    }, 500);
  }

  async renderStripeCheckout(): Promise<void> {
    this.loading = true;
    this.isStripeSessionActive = true;
    try {
      const keyResponse = await this.dqfService.getStripePublishableKey().toPromise();
      this.publicKey = keyResponse.publicKey;

      const stripe = await loadStripe(this.publicKey);
      if (!stripe) {
        throw new Error('Stripe failed to initialize');
      }
      const origin = window.location.origin;
      const successReturnUrl = `${origin}/#/driverQualification/redirect-stripe?session_id={CHECKOUT_SESSION_ID}`;
      const sessionResponse = await this.dqfService
        .createStripePaymentSession({
          successReturnUrl: successReturnUrl,
          product: POLICE_CLEARANCE_CERTIFICATE,
          userId: this.driver.id,
          customerEmail: this.driver.email
        })
        .toPromise();
      sessionStorage.setItem('stripeCheckoutSessionResponse', JSON.stringify(sessionResponse));
      sessionStorage.setItem('postStripeReturnRoute', '/paymentStatus');
      const clientSecret = sessionResponse.paymentSessionKey;
      this.checkout?.unmount();
      this.checkout = await stripe.initEmbeddedCheckout({
        fetchClientSecret: async () => clientSecret
      });
      setTimeout(() => {
        if (this.checkout && this.checkoutRef?.nativeElement) {
          this.checkout.mount(this.checkoutRef.nativeElement);
          this.isCheckoutMounted = true;
        }
      }, 0);
      this.isCheckoutMounted = true;
    } catch (error) {
      this.toastService.showError('Payment setup failed. Please try again.');
      this.isStripeSessionActive = false;
    } finally {
      this.loading = false;
    }
  }
  private safeUnmountCheckout(): void {
    try {
      if (this.checkout && this.isCheckoutMounted) {
        this.checkout.unmount();
        this.checkout.destroy();
      }
    } catch (error) {
      return error;
    } finally {
      this.isCheckoutMounted = false;
      this.checkout = null;
    }

    if (this.checkoutRef?.nativeElement) {
      this.checkoutRef.nativeElement.innerHTML = '';
    }
    const container = this.checkoutRef?.nativeElement;
    if (container) {
      container.innerHTML = '';
    }

    this.checkout = null;
    this.isCheckoutMounted = false;
  }
}
